/**
 * One way to show dates. Formatted by hand rather than with toLocale* so the
 * output is identical in every browser and locale ("Sep", never "Sept").
 * Payroll weeks are UTC Monday–Sunday on the backend, so week labels use UTC;
 * everything else renders in the viewer's local time.
 */
const DAY = 24 * 60 * 60 * 1000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function toDate(v: string | number | Date | null | undefined): Date | null {
  if (v === null || v === undefined || v === '') return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

const pad = (n: number): string => String(n).padStart(2, '0');

/** "29 Sep 2026" */
export function formatDate(v: string | number | Date | null | undefined, fallback = '—'): string {
  const d = toDate(v);
  return d ? `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}` : fallback;
}

/** "29 Sep 2026, 14:05" */
export function formatDateTime(v: string | number | Date | null | undefined, fallback = '—'): string {
  const d = toDate(v);
  return d ? `${formatDate(d)}, ${pad(d.getHours())}:${pad(d.getMinutes())}` : fallback;
}

/** Whole days since a timestamp, for "in stage for N days" style labels. */
export function daysSince(v: string | number | Date | null | undefined, now: Date = new Date()): number | null {
  const d = toDate(v);
  return d ? Math.max(0, Math.floor((now.getTime() - d.getTime()) / DAY)) : null;
}

/**
 * A payroll week as an explicit range: "Mon 21 – Sun 27 Sep 2026", or
 * "Mon 28 Sep – Sun 4 Oct 2026" across months. `weekOf` is the backend's
 * UTC Monday for the week (date or timestamp).
 */
export function formatWeekRange(weekOf: string | null | undefined, fallback = '—'): string {
  const start = toDate(weekOf ? `${String(weekOf).slice(0, 10)}T00:00:00Z` : null);
  if (!start) return fallback;
  const end = new Date(start.getTime() + 6 * DAY);
  const day = (d: Date): string => `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()}`;
  const sameMonth = start.getUTCMonth() === end.getUTCMonth();
  const left = sameMonth ? day(start) : `${day(start)} ${MONTHS[start.getUTCMonth()]}`;
  return `${left} – ${day(end)} ${MONTHS[end.getUTCMonth()]} ${end.getUTCFullYear()}`;
}
